import { useCallback, useEffect, useRef, useState } from 'react';
import { startNoise, type NoiseControls } from './noise.js';
import { createTrack, DEFAULT_TRACK, type Track } from './track.js';

/**
 * The room tone: a YouTube track and a noise bed, mixed against each other.
 *
 * Settings live in this browser rather than on the server. They are a property
 * of where you are sitting — headphones at a desk want a different mix from a
 * phone on a train — and they should not follow you between the two.
 */
export interface AtmosphereState {
  url: string;
  volume: number;
  playing: boolean;
  noiseVolume: number;
  noiseTone: number;
}

const KEY = 'map.atmosphere';

const DEFAULTS: AtmosphereState = {
  url: DEFAULT_TRACK,
  volume: 0.5,
  playing: false,
  noiseVolume: 0,
  noiseTone: 0.35,
};

function read(): AtmosphereState {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    // Never trust what comes back: storage survives across versions.
    const saved = { ...DEFAULTS, ...(JSON.parse(raw) as Partial<AtmosphereState>), playing: false };
    // An emptied field used to be saved as '', which then played the default
    // while showing nothing; show what will actually play.
    return { ...saved, url: saved.url.trim() || DEFAULT_TRACK };
  } catch {
    return DEFAULTS;
  }
}

export function useAtmosphere() {
  const [state, setState] = useState<AtmosphereState>(read);
  const [error, setError] = useState<string | null>(null);
  const audio = useRef<Track | null>(null);
  const noise = useRef<NoiseControls | null>(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify({ ...state, playing: false }));
    } catch {
      // Private browsing, or storage turned off. The mix still works for now.
    }
  }, [state]);

  const update = useCallback((patch: Partial<AtmosphereState>) => {
    setState((current) => ({ ...current, ...patch }));
  }, []);

  // --- the track ---------------------------------------------------------
  const play = useCallback(async () => {
    const url = state.url.trim() || DEFAULT_TRACK;
    setError(null);
    try {
      if (!audio.current) audio.current = createTrack(url, state.volume);
      else audio.current.load(url);
      audio.current.setVolume(state.volume);
      await audio.current.play();
      update({ url, playing: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That would not play.');
    }
  }, [state.url, state.volume, update]);

  const pause = useCallback(() => {
    audio.current?.pause();
    update({ playing: false });
  }, [update]);

  useEffect(() => {
    audio.current?.setVolume(state.volume);
  }, [state.volume]);

  // --- the noise bed -----------------------------------------------------
  useEffect(() => {
    if (state.noiseVolume <= 0) {
      noise.current?.stop();
      noise.current = null;
      return;
    }
    if (!noise.current) noise.current = startNoise(state.noiseVolume, state.noiseTone);
    else noise.current.setVolume(state.noiseVolume);
  }, [state.noiseVolume, state.noiseTone]);

  useEffect(() => {
    noise.current?.setTone(state.noiseTone);
  }, [state.noiseTone]);

  // Noise restored from a saved mix starts before any click, so the browser
  // holds it silent; the first touch anywhere on the page lets it through.
  useEffect(() => {
    const wake = () => noise.current?.wake();
    window.addEventListener('pointerdown', wake);
    window.addEventListener('keydown', wake);
    return () => {
      window.removeEventListener('pointerdown', wake);
      window.removeEventListener('keydown', wake);
    };
  }, []);

  useEffect(
    () => () => {
      noise.current?.stop();
      audio.current?.destroy();
    },
    [],
  );

  return { state, update, play, pause, error };
}
